import { Controller } from "@hotwired/stimulus";
import { application } from "src";
import { isPresent, isBlank, toMilliseconds, isBetween } from "@fretadao/f-js-utils";
import { addClass, removeClass, faIcon, hasClass, getParent } from "@fretadao/f-js-dom";
import { API } from '../../../src/api';

application.register('ride-request-guest-trip', class extends Controller {
  static targets = [
    'startAddress', 'startLatitude', 'startLongitude', 'endAddress', 'endLatitude', 'endLongitude',
    'mapContainer', 'formContainer', 'buttonContainer', 'lastAddresses'
  ];
  static values = {
    openSelectAddress: { type: Boolean, default: false }
  }

  defaultCoords = [-22.0699816, -48.4357514];
  animationDurationInMs = toMilliseconds(1.2, 'seconds');
  defaultDelayInMs = 300;
  distanceToIncreaseCoords = 0.00050;
  minCharsToConsiderAsChangedAddress = 30;

  connect() {
    this.#getForCurrentLocation();

    this.#setup();
    addEventListener('resize', this.#setup.bind(this));

    this.startAddressTarget.addEventListener('focus', this.#focusField.bind(this));
    this.endAddressTarget.addEventListener('focus', this.#focusField.bind(this));

    this.startAddressTarget.addEventListener('input', this.#changeLatLongAndGetLocations.bind(this));
    this.endAddressTarget.addEventListener('input', this.#changeLatLongAndGetLocations.bind(this));

    this.mapContainerTarget.addEventListener('geocomplete:markerMoved:marker_start', (event) => {
      this.startLatitudeTarget.value = event.detail.lat;
      this.startLongitudeTarget.value = event.detail.lng;
      this.#getAddressFromCoordinates(
        this.startLatitudeTarget, this.startLongitudeTarget, this.startAddressTarget
      );
    });
    this.mapContainerTarget.addEventListener('geocomplete:markerMoved:marker_end', (event) => {
      this.endLatitudeTarget.value = event.detail.lat;
      this.endLongitudeTarget.value = event.detail.lng;
      this.#getAddressFromCoordinates(
        this.endLatitudeTarget, this.endLongitudeTarget, this.endAddressTarget
      );
    });
  }

  swapAddresses() {
    const startAddress = this.startAddressTarget.value;
    const startLatitude = this.startLatitudeTarget.value;
    const startLongitude = this.startLongitudeTarget.value;

    this.startAddressTarget.value = this.endAddressTarget.value;
    this.startLatitudeTarget.value = this.endLatitudeTarget.value;
    this.startLongitudeTarget.value = this.endLongitudeTarget.value;
    this.endAddressTarget.value = startAddress;
    this.endLatitudeTarget.value = startLatitude;
    this.endLongitudeTarget.value = startLongitude;

    setTimeout(() => {
      this.startLatitudeTarget.dispatchEvent(new Event('change'));
      this.startLongitudeTarget.dispatchEvent(new Event('change'));
      this.endLatitudeTarget.dispatchEvent(new Event('change'));
      this.endLongitudeTarget.dispatchEvent(new Event('change'));
    }, this.defaultDelayInMs);
  }

  fillAddress(event) {
    let addressElement = event.target;
    let latitudeField, longitudeField, otherField;

    while(!hasClass(addressElement, 'item-box')) addressElement = getParent(addressElement);

    if (this.#fieldIsStartAddress(this.currentField)) {
      latitudeField = this.startLatitudeTarget;
      longitudeField = this.startLongitudeTarget;
      otherField = this.endAddressTarget;
    } else {
      latitudeField = this.endLatitudeTarget;
      longitudeField = this.endLongitudeTarget;
      otherField = this.startAddressTarget;
    }

    const coords = addressElement.dataset.coords.split(',');
    latitudeField.value = coords[0]
    longitudeField.value = coords[1];
    this.currentField.value = addressElement.title;

    setTimeout(() => {
      latitudeField.dispatchEvent(new Event('change'));
      longitudeField.dispatchEvent(new Event('change'));
    }, this.defaultDelayInMs);

    if (isBlank(otherField.value)) {
      otherField.focus();
      this.#getLocations();
    }

    this.#checkIfCanClose();
  }

  #suggestedCoords() {
    let latitudeField, longitudeField, otherLatitudeField, otherLongitudeField;

    if (this.#fieldIsStartAddress(this.currentField)) {
      latitudeField = this.startLatitudeTarget;
      longitudeField = this.startLongitudeTarget;
      otherLatitudeField = this.endLatitudeTarget;
      otherLongitudeField = this.endLongitudeTarget;
    } else {
      latitudeField = this.endLatitudeTarget;
      longitudeField = this.endLongitudeTarget;
      otherLatitudeField = this.startLatitudeTarget;
      otherLongitudeField = this.startLongitudeTarget;
    }

    if (isPresent(latitudeField.value) && isPresent(longitudeField.value)) return [latitudeField.value, longitudeField.value];

    if (isPresent(otherLatitudeField.value) && isPresent(otherLongitudeField.value)) {
      let newLatitude = parseFloat(otherLatitudeField.value);
      let newLongitude = parseFloat(otherLongitudeField.value);

      return [newLatitude + this.distanceToIncreaseCoords, newLongitude + this.distanceToIncreaseCoords];
    }

    if (isPresent(this.currentLocation)) {
      return [this.currentLocation.coords.latitude, this.currentLocation.coords.longitude];
    }

    return this.defaultCoords;
  }

  setOnMap() {
    const addressField = this.currentField;
    let latitudeField, longitudeField;

    if (this.#fieldIsStartAddress(addressField)) {
      latitudeField = this.startLatitudeTarget;
      longitudeField = this.startLongitudeTarget;
    } else {
      latitudeField = this.endLatitudeTarget;
      longitudeField = this.endLongitudeTarget;
    }

    if (isPresent(addressField.value) && isBlank(latitudeField.value)) addressField.value = '';

    if (isBlank(addressField.value)) {
      const suggestedCoords = this.#suggestedCoords();

      latitudeField.value = suggestedCoords[0];
      longitudeField.value = suggestedCoords[1];

      this.#getAddressFromCoordinates(latitudeField, longitudeField, addressField);
    }

    setTimeout(() => {
      latitudeField.dispatchEvent(new Event('change'));
      longitudeField.dispatchEvent(new Event('change'));
    }, this.defaultDelayInMs);

    this.#closeSelectAddress();
  }

  #fieldIsStartAddress(field) {
    return field === this.startAddressTarget;
  }

  #setup() {
    this.#setElementOnFullWidth();
    this.#setMapHeight();
    this.selectAddressTop ||= this.formContainerTarget.offsetTop;
    this.selectAddressHeight ||= this.formContainerTarget.offsetHeight;
    this.#checkIfCanSubmit();
  }

  #getForCurrentLocation() {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition((position) => {
      this.currentLocation = position;
      this.#getAddressFromCurrentLocation();
    });
  }

  #getAddressFromCurrentLocation() {
    if (!this.currentLocation) return;

    this.startLatitudeTarget.value = this.currentLocation.coords.latitude;
    this.startLongitudeTarget.value = this.currentLocation.coords.longitude;

    this.#getAddressFromCoordinates(
      this.startLatitudeTarget, this.startLongitudeTarget, this.startAddressTarget
    )

    setTimeout(() => {
      this.startLatitudeTarget.dispatchEvent(new Event('change'));
      this.startLongitudeTarget.dispatchEvent(new Event('change'));
    }, this.defaultDelayInMs);
  }

  #getAddressFromCoordinates(fieldLatitude, fieldLongitude, fieldAddress) {
    addClass(fieldAddress, 'animate-pulse');

    API.rideRequest.api.reverseGeocode(fieldLatitude.value, fieldLongitude.value).then((geocodedCoordinates) => {
      fieldAddress.value = geocodedCoordinates.fullAddress;
      removeClass(fieldAddress, 'animate-pulse');
    });
  }

  #openSelectAddress() {
    if (this.openSelectAddressValue) return this.#getLocations();

    this.openSelectAddressValue = true;

    this.formContainerTarget.style.position = 'absolute';
    addClass(this.formContainerTarget, 'md:w-5/12');

    const animation = this.formContainerTarget.animate(
      [
        { top: `${this.selectAddressTop}px`, minHeight: `${this.selectAddressHeight}px` },
        { top: '0px', minHeight: '100dvh' }
      ],
      {
        duration: this.animationDurationInMs,
        easing: 'ease-in-out'
      }
    );
    animation.onfinish = () => {
      // workaround to leave the focus on input, because of the animation the input goes to the top
      // and scrollTo doesn't work on mobile browsers 🫤
      document.querySelector('#logo_fretadao').focus();
      this.currentField.focus();
      this.formContainerTarget.style.top =  '0px';
      this.formContainerTarget.style.minHeight = '100dvh';
      removeClass(this.formContainerTarget, 'shadow-top');
      addClass(this.formContainerTarget, 'md:shadow-lg');
      addClass(this.buttonContainerTarget, 'hidden');
      removeClass(this.lastAddressesTarget, 'hidden');
      this.#getLocations();
    };
  }

  #closeSelectAddress() {
    if (!this.openSelectAddressValue) return;

    this.openSelectAddressValue = false;

    const animation = this.formContainerTarget.animate(
      [
        { top: '0px' },
        { top: `${this.selectAddressTop}px`, minHeight: `${this.selectAddressHeight}px` },
      ],
      {
        duration: this.animationDurationInMs,
        easing: 'ease-in-out'
      }
    );
    animation.onfinish = () => {
      this.formContainerTarget.style.top = '0px';
      this.formContainerTarget.style.position = 'relative';
      this.formContainerTarget.style.minHeight = `${this.selectAddressHeight}px`;
      addClass(this.formContainerTarget, 'shadow-top');
      removeClass(this.formContainerTarget, 'md:shadow-lg');
      removeClass(this.formContainerTarget, 'md:w-5/12');
      addClass(this.lastAddressesTarget, 'hidden');
      this.#checkIfCanSubmit();
    };
  }

  #setMapHeight() {
    const windowHeight = window.innerHeight;
    const formHeight = this.formContainerTarget.offsetHeight;
    const mapOffsetTop = this.mapContainerTarget.offsetTop;
    let mapHeight = parseFloat(windowHeight - mapOffsetTop - formHeight);
    const minMapHeight = 340;

    if (mapHeight < minMapHeight) mapHeight = minMapHeight;

    this.mapContainerTarget.style.height = `${mapHeight}px`;
  }

  #setElementOnFullWidth() {
    const width = document.querySelector('#container').offsetWidth;
    this.element.style.width = `${width}px`;
  }

  #changeLatLongAndGetLocations() {
    if (this.currentField.value.trim().length <= this.minCharsToConsiderAsChangedAddress) {
      if (this.#fieldIsStartAddress(this.currentField)) {
        this.startLatitudeTarget.value = '';
        this.startLongitudeTarget.value = '';
      } else {
        this.endLatitudeTarget.value = '';
        this.endLongitudeTarget.value = '';
      }
    }

    this.#getLocations();
  }

  // TODO: verificar como cancelar a req anterior
  #getLocations() {
    const query = this.currentField.value.trim();

    if (isBetween(query.length, 1, 2)) return;

    this.#startLoadingLocations();

    API.rideRequest.api.locations(query).then((response) => {
      this.#showLocations(response.data);
    });
  }

  #startLoadingLocations() {
    const item = () => {
      const containerBox = document.createElement('div');
      addClass(
        containerBox,
        ['flex', 'items-center', 'justify-between', 'border-t', 'border-gray-lt', 'p-2', 'cursor-wait', 'animate-pulse']
      );

      const icon = document.createElement('i');
      addClass(icon, ['mr-2', 'bg-gray-lt', 'rounded-full', 'p-4']);

      const addressText = document.createElement('span');
      addClass(addressText, ['w-full', 'bg-gray-lt', 'p-1.5', 'rounded-full']);

      const container = document.createElement('div');
      addClass(container, ['flex', 'items-center', 'w-full']);

      container.append(icon);
      container.append(addressText);

      containerBox.append(container);

      return containerBox;
    }

    this.#clearLocations();

    for (let i = 1; i <= 5; i++) {
      this.lastAddressesTarget.append(item());
    }
  }

  #clearLocations() {
    Array.from(this.lastAddressesTarget.children).forEach((item) => item.remove());
  }

  #showLocations(locations) {
    this.#clearLocations();

    const mountItem = (item) => {
      const containerBox = document.createElement('div');
      addClass(
        containerBox,
        [
          'flex', 'items-center', 'justify-between', 'border-t',
          'border-gray-lt', 'p-2', 'cursor-pointer', 'hover:bg-gray-lt', 'item-box'
        ]
      );
      containerBox.dataset.action = item.action || 'click->ride-request-guest-trip#fillAddress';
      containerBox.dataset.coords = item.coordinates;
      containerBox.title = item.title;

      const kindIcon = item.lasts ? 'clock' : 'location-dot';
      const faKind = item.lasts ? 'fa-regular' : 'fa-solid';
      const icon = faIcon(kindIcon, { class: [faKind, 'mr-2', 'bg-gray-lt', 'rounded-full', 'p-2'] });

      const addressText = document.createElement('span');
      addressText.textContent = item.title;
      if (item.class) addClass(addressText, item.class);

      const container = document.createElement('div');
      addClass(container, ['flex', 'items-center']);

      container.append(icon);
      container.append(addressText);

      containerBox.append(container);

      return containerBox;
    }

    locations.forEach((item) => this.lastAddressesTarget.append(mountItem(item)));

    const setOnMap = {
      title: 'Definir no mapa',
      class: 'font-bold',
      action: 'click->ride-request-guest-trip#setOnMap'
    };

    this.lastAddressesTarget.append(mountItem(setOnMap));
  }

  #focusField(event) {
    const field = event.target;

    if (this.openSelectAddressValue && this.currentField === field) return;

    const otherField = this.#fieldIsStartAddress(field) ? this.endAddressTarget : this.startAddressTarget;

    removeClass(otherField, ['border-3', 'border-black']);

    this.currentField = field;
    addClass(field, ['border-3', 'border-black']);
    this.#openSelectAddress();
  }

  #checkIfCanClose() {
    if (isBlank(this.startAddressTarget.value) || isBlank(this.endAddressTarget.value)) return;

    removeClass(this.currentField, ['border-3', 'border-black']);

    this.#closeSelectAddress();
  }

  #checkIfCanSubmit() {
    if (this.openSelectAddressValue || isBlank(this.startAddressTarget.value) || isBlank(this.endAddressTarget.value)) return;

    removeClass(this.buttonContainerTarget, 'hidden');
    this.#setMapHeight();
  }
});
